// PostSuggestedListing.js

import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Modal,
  Alert,
  Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbayadmin.com/APIControllers';

export default function PostSuggestedListing({ navigation }) {
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [offer, setOffer] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showLaunchOptions, setShowLaunchOptions] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [form, setForm] = useState({
    ListingID: '',
    BrandName: '',
    Location: '',
    City: '',
    Neighbourhood: '',
    ListingDescription: '',
    ContentBrief: '',
    NonCreditOffer: '',
    Credits: '',
    PreferredAvailability: '',
    OfferCategory: '',
    MinFollowersRequired: '',
    AdditionalBookingDetails: '',
    BrandURL: ''
  });

  useEffect(() => {
    fetchSuggestedListing();
  }, []);

  const fetchSuggestedListing = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE}/GetBrandSuggestedListing.php`, {
        method: 'GET',
        credentials: 'include'
      });

      const data = await response.json();

      if (!data.success) {
        setOffer(null);
        setLoading(false);
        return;
      }

      const listing = data.listing;

      setOffer(listing);
      setIsSubscribed(data.isSubscribed === true);

      setForm({
        ListingID: listing.ListingID || '',
        BrandName: listing.BrandName || '',
        Location: listing.Location || '',
        City: listing.City || '',
        Neighbourhood: listing.Neighbourhood || '',
        ListingDescription: listing.ListingDescription || '',
        ContentBrief: listing.ContentBrief || '',
        NonCreditOffer: listing.NonCreditOffer || '',
        Credits: String(listing.Credits || ''),
        PreferredAvailability: listing.PreferredAvailability || '',
        OfferCategory: listing.OfferCategory || '',
        MinFollowersRequired: listing.MinFollowersRequired || '',
        AdditionalBookingDetails: listing.AdditionalBookingDetails || '',
        BrandURL: listing.BrandURL || ''
      });
    } catch (error) {
      Alert.alert('Error', 'Could not load your suggested listing.');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const validateForm = () => {
    if (!form.NonCreditOffer.trim()) {
      Alert.alert('Missing Offer', 'Please add what creators will receive.');
      return false;
    }

    if (!form.Credits.trim()) {
      Alert.alert('Missing Value', 'Please add the estimated offer value.');
      return false;
    }

    if (!termsAccepted) {
      Alert.alert('Terms Required', 'Please confirm you accept the Collabbay terms before posting.');
      return false;
    }

    return true;
  };

  const postSuggestedListing = async () => {
    if (posting) return;

    if (!validateForm()) return;

    setPosting(true);

    try {
      const response = await fetch(`${API_BASE}/BrandPostSuggestedListing.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (!data.success) {
        Alert.alert('Error', data.error || 'Could not post your suggested listing.');
        setPosting(false);
        return;
      }

      if (isSubscribed) {
        Alert.alert(
          'Offer Posted',
          'Your offer is now live.',
          [
            {
              text: 'View My Offers',
              onPress: () => navigation.navigate('MyOffers', { forceRefresh: true })
            }
          ]
        );
        setPosting(false);
        return;
      }

      setShowLaunchOptions(true);
    } catch (error) {
      Alert.alert('Error', 'Network error. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  const goToSubscribe = () => {
    setShowLaunchOptions(false);

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.href = 'https://collabbayadmin.com/subscribe';
      return;
    }

    navigation.navigate('Subscribe');
  };

  const goToMyOffers = () => {
    setShowLaunchOptions(false);
    navigation.navigate('MyOffers', { forceRefresh: true });
  };

  if (loading) {
    return (
      <View style={styles.loadingWrapper}>
        <ActivityIndicator size="large" color="#111" />
        <Text style={styles.loadingText}>Loading your suggested listing...</Text>
      </View>
    );
  }

  if (!offer) {
    return (
      <View style={styles.emptyWrapper}>
        <View style={styles.emptyCard}>
          <Ionicons name="sparkles-outline" size={42} color="#111" />
          <Text style={styles.emptyTitle}>No suggested listings yet</Text>
          <Text style={styles.emptyText}>
            If we prepare a suggested offer for your account, it will appear here.
          </Text>

          <TouchableOpacity
            style={styles.emptyButton}
            onPress={() => navigation.navigate('BusinessHomepage')}
          >
            <Text style={styles.emptyButtonText}>Back to dashboard</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.wrapper}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={20} color="#111" />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>

        <View style={styles.heroCard}>
          <View style={styles.heroBadge}>
            <Ionicons name="sparkles-outline" size={15} color="#111" />
            <Text style={styles.heroBadgeText}>Curated by collabbay™</Text>
          </View>

          <Text style={styles.heroTitle}>Your suggested offer is ready.</Text>

          <Text style={styles.heroBody}>
            Based on your account details, our agents have personally prepared an offer that could be a good fit for your brand.
            You can amend the offer as you wish, or post it as is.
          </Text>

          <Text style={styles.heroBody}>
            Once you're happy, just click post. Your offer will appear in the marketplace, and creators can start requesting bookings.
          </Text>
        </View>

        <View style={styles.offerSummaryCard}>
          <Text style={styles.sectionTitle}>Offer summary</Text>

          <View style={styles.summaryRow}>
            <View style={styles.summaryIcon}>
              <Ionicons name="gift-outline" size={18} color="#111" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.summaryLabel}>Suggested gift</Text>
              <Text style={styles.summaryValue}>{form.NonCreditOffer || 'Not set'}</Text>
            </View>
          </View>

          <View style={styles.summaryRow}>
            <View style={styles.summaryIcon}>
              <Ionicons name="cash-outline" size={18} color="#111" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.summaryLabel}>Estimated value</Text>
              <Text style={styles.summaryValue}>£{form.Credits || '0'}</Text>
            </View>
          </View>

          <View style={styles.summaryRow}>
            <View style={styles.summaryIcon}>
              <Ionicons name="videocam-outline" size={18} color="#111" />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.summaryLabel}>Creator deliverable</Text>
              <Text style={styles.summaryValue}>You will get one promotional reel or content post.</Text>
            </View>
          </View>
        </View>

        <View style={styles.formCard}>
          <Text style={styles.sectionTitle}>Amend your offer</Text>
          <Text style={styles.sectionSubText}>
            These fields are editable. Leave them as they are if you’re happy with our suggestion.
          </Text>

          <Text style={styles.label}>What creators receive</Text>
          <TextInput
            value={form.NonCreditOffer}
            onChangeText={(value) => updateField('NonCreditOffer', value)}
            style={styles.input}
            placeholder="E.g. 2 mains, 2 drinks"
            multiline
          />

          <Text style={styles.label}>Offer value (£)</Text>
          <TextInput
            value={form.Credits}
            onChangeText={(value) => updateField('Credits', value.replace(/[^0-9]/g, ''))}
            style={styles.input}
            placeholder="E.g. 70"
            keyboardType="numeric"
          />

          <Text style={styles.label}>Content brief</Text>
          <TextInput
            value={form.ContentBrief}
            onChangeText={(value) => updateField('ContentBrief', value)}
            style={[styles.input, styles.textArea]}
            placeholder="What should creators include?"
            multiline
          />

          <Text style={styles.label}>Preferred availability / open hours</Text>
          <TextInput
            value={form.PreferredAvailability}
            onChangeText={(value) => updateField('PreferredAvailability', value)}
            style={[styles.input, styles.textArea]}
            placeholder="E.g. Monday - Friday, 12pm - 5pm"
            multiline
          />

          <Text style={styles.label}>Location</Text>
          <TextInput
            value={form.Location}
            onChangeText={(value) => updateField('Location', value)}
            style={styles.input}
            placeholder="Venue address"
          />

          <Text style={styles.label}>Short description</Text>
          <TextInput
            value={form.ListingDescription}
            onChangeText={(value) => updateField('ListingDescription', value)}
            style={[styles.input, styles.largeTextArea]}
            placeholder="Describe your venue"
            multiline
          />

          <TouchableOpacity
            activeOpacity={0.85}
            style={styles.checkboxRow}
            onPress={() => setTermsAccepted(!termsAccepted)}
          >
            <View style={[styles.checkbox, termsAccepted && styles.checkboxActive]}>
              {termsAccepted && <Ionicons name="checkmark" size={15} color="#111" />}
            </View>

            <Text style={styles.checkboxText}>
              By posting this offer, I confirm I accept the Collabbay terms of use and consent to this offer being shown publicly in the marketplace.
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.postButton, posting && { opacity: 0.65 }]}
            onPress={postSuggestedListing}
            disabled={posting}
          >
            {posting ? (
              <ActivityIndicator color="#111" />
            ) : (
              <>
                <Text style={styles.postButtonText}>Post my offer</Text>
                <Ionicons name="arrow-forward" size={18} color="#111" />
              </>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      <Modal visible={showLaunchOptions} transparent animationType="fade">
        <View style={styles.modalOverlay}>
          <View style={styles.launchModal}>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.successIcon}>
                <Ionicons name="checkmark-circle" size={68} color="#1f8f3a" />
              </View>

              <Text style={styles.niceWork}>Nice work.</Text>
              <Text style={styles.modalTitle}>Your offer is posted</Text>

              <Text style={styles.modalSubText}>
                Choose how you want your listing to sit in the marketplace.
              </Text>

              <TouchableOpacity activeOpacity={0.9} style={styles.standardCard} onPress={goToSubscribe}>
                <View style={styles.optionHeader}>
                  <View>
                    <Text style={styles.optionTitle}>Standard Listing</Text>
                    <Text style={styles.optionTag}>Recommended</Text>
                  </View>

                  <View style={styles.freeBadge}>
                    <Text style={styles.freeBadgeText}>TRY</Text>
                  </View>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="sparkles-outline" size={17} color="#111" />
                  <Text style={styles.featureText}>My offer gets premium creator spaces</Text>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="trending-up-outline" size={17} color="#111" />
                  <Text style={styles.featureText}>My offer gets priority ranking among 200+ offers</Text>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="people-outline" size={17} color="#111" />
                  <Text style={styles.featureText}>I can handpick and invite creators to my offer</Text>
                </View>

                <View style={styles.standardButton}>
                  <Text style={styles.standardButtonText}>Post as Standard</Text>
                </View>
              </TouchableOpacity>

              <TouchableOpacity activeOpacity={0.9} style={styles.restrictedCard} onPress={goToMyOffers}>
                <View style={styles.optionHeader}>
                  <View>
                    <Text style={styles.optionTitle}>Restricted Listing</Text>
                    <Text style={styles.optionTagMuted}>Basic access</Text>
                  </View>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="checkmark-outline" size={17} color="#333" />
                  <Text style={styles.restrictedText}>Creators can view and book</Text>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="remove-outline" size={17} color="#777" />
                  <Text style={styles.mutedFeatureText}>Restricted visibility</Text>
                </View>

                <View style={styles.featureRow}>
                  <Ionicons name="remove-outline" size={17} color="#777" />
                  <Text style={styles.mutedFeatureText}>Capped booking volume</Text>
                </View>

                <View style={styles.restrictedButton}>
                  <Text style={styles.restrictedButtonText}>Continue as Restricted</Text>
                </View>
              </TouchableOpacity>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
    backgroundColor: '#f0f0f0'
  },

  container: {
    width: '100%',
    maxWidth: 760,
    alignSelf: 'center',
    padding: 16,
    paddingBottom: 50
  },

  loadingWrapper: {
    flex: 1,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20
  },

  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#555',
    fontWeight: '600'
  },

  emptyWrapper: {
    flex: 1,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18
  },

  emptyCard: {
    backgroundColor: '#fff',
    borderRadius: 22,
    padding: 24,
    alignItems: 'center',
    width: '100%',
    maxWidth: 430,
    borderWidth: 1,
    borderColor: '#e8e8e8'
  },

  emptyTitle: {
    fontSize: 20,
    fontWeight: '900',
    marginTop: 12,
    color: '#111'
  },

  emptyText: {
    fontSize: 13,
    color: '#555',
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 19
  },

  emptyButton: {
    marginTop: 18,
    backgroundColor: '#fdf05d',
    borderRadius: 12,
    paddingVertical: 13,
    paddingHorizontal: 18,
    borderWidth: 1,
    borderColor: '#111'
  },

  emptyButtonText: {
    fontWeight: '900',
    color: '#111'
  },

  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: 12,
    paddingVertical: 8
  },

  backText: {
    marginLeft: 6,
    fontWeight: '700',
    color: '#111'
  },

  heroCard: {
    backgroundColor: '#111',
    borderRadius: 24,
    padding: 22,
    marginBottom: 14
  },

  heroBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#fdf05d',
    borderRadius: 999,
    paddingHorizontal: 11,
    paddingVertical: 7,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14
  },

  heroBadgeText: {
    marginLeft: 5,
    color: '#111',
    fontWeight: '900',
    fontSize: 12
  },

  heroTitle: {
    color: '#fff',
    fontSize: 27,
    fontWeight: '900',
    lineHeight: 32,
    marginBottom: 10
  },

  heroBody: {
    color: '#dedede',
    fontSize: 14,
    lineHeight: 21,
    marginBottom: 8,
    fontWeight: '500'
  },

  offerSummaryCard: {
    backgroundColor: '#fff',
    borderRadius: 22,
    padding: 18,
    marginBottom: 14,
    borderWidth: 1,
    borderColor: '#e8e8e8'
  },

  sectionTitle: {
    fontSize: 18,
    fontWeight: '900',
    color: '#111',
    marginBottom: 10
  },

  sectionSubText: {
    fontSize: 13,
    color: '#666',
    lineHeight: 18,
    marginBottom: 16
  },

  summaryRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#fafafa',
    borderRadius: 16,
    padding: 13,
    marginBottom: 9,
    borderWidth: 1,
    borderColor: '#efefef'
  },

  summaryIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#fdf05d',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10
  },

  summaryLabel: {
    fontSize: 12,
    color: '#777',
    fontWeight: '800',
    marginBottom: 3
  },

  summaryValue: {
    fontSize: 14,
    color: '#111',
    fontWeight: '800',
    lineHeight: 19
  },

  formCard: {
    backgroundColor: '#fff',
    borderRadius: 22,
    padding: 18,
    borderWidth: 1,
    borderColor: '#e8e8e8'
  },

  label: {
    fontSize: 13,
    fontWeight: '900',
    color: '#111',
    marginBottom: 7,
    marginTop: 4
  },

  input: {
    backgroundColor: '#f6f6f6',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#ddd',
    padding: 13,
    fontSize: 14,
    color: '#111',
    marginBottom: 13,
    minHeight: 47
  },

  textArea: {
    minHeight: 86,
    textAlignVertical: 'top'
  },

  largeTextArea: {
    minHeight: 120,
    textAlignVertical: 'top'
  },

  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#fafafa',
    borderRadius: 15,
    padding: 13,
    borderWidth: 1,
    borderColor: '#ececec',
    marginTop: 4,
    marginBottom: 15
  },

  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 7,
    borderWidth: 1.5,
    borderColor: '#111',
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff'
  },

  checkboxActive: {
    backgroundColor: '#fdf05d'
  },

  checkboxText: {
    flex: 1,
    fontSize: 12,
    color: '#333',
    lineHeight: 18,
    fontWeight: '600'
  },

  postButton: {
    backgroundColor: '#fdf05d',
    borderRadius: 15,
    paddingVertical: 15,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#111',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  },

  postButtonText: {
    fontSize: 15,
    fontWeight: '900',
    color: '#111',
    marginRight: 7
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.62)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 14
  },

  launchModal: {
    backgroundColor: '#fff',
    borderRadius: 26,
    width: '100%',
    maxWidth: 620,
    maxHeight: '92%',
    padding: 20,
    borderWidth: 1,
    borderColor: '#e8e8e8'
  },

  successIcon: {
    alignItems: 'center',
    marginBottom: 2
  },

  niceWork: {
    color: '#1f8f3a',
    fontWeight: '900',
    textAlign: 'center',
    fontSize: 13,
    marginBottom: 4
  },

  modalTitle: {
    fontSize: 23,
    fontWeight: '900',
    textAlign: 'center',
    color: '#111',
    marginBottom: 6
  },

  modalSubText: {
    fontSize: 13,
    color: '#555',
    textAlign: 'center',
    lineHeight: 18,
    marginBottom: 16
  },

  standardCard: {
    backgroundColor: '#effff3',
    borderColor: '#28a745',
    borderWidth: 1,
    borderRadius: 20,
    padding: 16,
    marginBottom: 12
  },

  restrictedCard: {
    backgroundColor: '#fff',
    borderColor: '#e5e5e5',
    borderWidth: 1,
    borderRadius: 20,
    padding: 16
  },

  optionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 13
  },

  optionTitle: {
    fontSize: 19,
    fontWeight: '900',
    color: '#111'
  },

  optionTag: {
    fontSize: 12,
    color: '#2f6f3e',
    fontWeight: '900',
    marginTop: 2
  },

  optionTagMuted: {
    fontSize: 12,
    color: '#666',
    fontWeight: '800',
    marginTop: 2
  },

  freeBadge: {
    backgroundColor: '#111',
    borderRadius: 999,
    paddingHorizontal: 13,
    paddingVertical: 8
  },

  freeBadgeText: {
    color: '#fdf05d',
    fontWeight: '900',
    fontSize: 12
  },

  featureRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 9
  },

  featureText: {
    marginLeft: 8,
    flex: 1,
    fontSize: 13,
    color: '#111',
    lineHeight: 18,
    fontWeight: '800'
  },

  restrictedText: {
    marginLeft: 8,
    flex: 1,
    fontSize: 13,
    color: '#333',
    lineHeight: 18,
    fontWeight: '700'
  },

  mutedFeatureText: {
    marginLeft: 8,
    flex: 1,
    fontSize: 13,
    color: '#777',
    lineHeight: 18,
    fontWeight: '600'
  },

  standardButton: {
    backgroundColor: '#fdf05d',
    borderRadius: 13,
    paddingVertical: 14,
    marginTop: 7,
    borderWidth: 1,
    borderColor: '#111'
  },

  standardButtonText: {
    textAlign: 'center',
    fontWeight: '900',
    color: '#111'
  },

  restrictedButton: {
    backgroundColor: '#f3f3f3',
    borderRadius: 13,
    paddingVertical: 14,
    marginTop: 7,
    borderWidth: 1,
    borderColor: '#e2e2e2'
  },

  restrictedButtonText: {
    textAlign: 'center',
    fontWeight: '900',
    color: '#111'
  }
});